import React, { useState } from "react";
import { Box, Group, Stack } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { useAuth } from "hooks/useAuth";
import { useInitialModals } from "hooks/useInitialModals";
import { ChartPanel } from "./UI/ChartPanel/ChartPanel";
import { AuthenticatedUserMenu } from "./UI/LeftMenu/AuthenticatedUserMenu";
import { UnauthenticatedUserMenu } from "./UI/LeftMenu/Unauthenticated/UnauthenticatedUserMenu";
import { LegendPanel } from "./UI/LegendPanel/LegendPanel";
import { TopEditMenu } from "./UI/TopEditMenu/TopEditMenu";
import { LeftMenuWrapper } from "./UI/Wrappers/LeftMenuWrapper";
import { MapWrapper } from "./UI/Wrappers/MapWrapper";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import { useMobileMenu } from "hooks/useMobileMenu";
import { PANEL_TOGGLE_ANIMATION_DURATION } from "config/constants";
import "./MainContainer.css";

const useNavbarItemStyles = createStyles(
  (
    theme,
    { active, isHovered }: { active: boolean; isHovered: boolean },
    u
  ) => ({
    link: {
      [u.dark]: {
        borderColor: isHovered || active ? "#7950f2" : theme.colors.dark[8],
        color: "#fff",
      },
      [u.light]: {
        borderColor: isHovered || active ? "#7950f2" : "#fff",
        color: active ? "#fff" : "#000",
      },
      backgroundColor: active ? "#7950f2" : "inherit",
      display: "block",
      padding: "8px 5px 5px 5px",
      width: "8em",
      borderBottom: "3px solid",
      textDecoration: "none",
      textAlign: "center",
      fontSize: "15px",
      lineHeight: "1.5em",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji",
      fontWeight: 400,
    },
  })
);

function NavbarItem({
  text,
  link,
  active = false,
}: {
  text: string;
  link: string;
  active?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { classes } = useNavbarItemStyles({ active, isHovered });

  return (
    <li
      className="item"
      role="none"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <a className={classes.link} role="menuitem" href={link}>
        {text}
      </a>
    </li>
  );
}

const useNavbarStyles = createStyles((theme, _, u) => ({
  container: {
    [u.dark]: {
      backgroundColor: theme.colors.dark[8],
    },
    [u.light]: {
      backgroundColor: "#fff",
    },
  },
  navbar: {
    display: "flex",
    justifyContent: "center",
    position: "absolute",
    boxShadow: "0 2px 4px -2px rgba(0, 0, 0, 0.3)",
    zIndex: 100,
    width: "100%",
    height: "38.5px",
  },
}));

function Navbar() {
  const isSmallViewport = useIsSmallViewport();
  const isRunningInsideElectron =
    /electron/i.test(navigator.userAgent) || globalThis.isElectron;
  const { classes, cx } = useNavbarStyles();

  return !isSmallViewport && !isRunningInsideElectron ? (
    <nav className={cx(classes.navbar, classes.container)}>
      <ul className="header-menu" role="menubar">
        <NavbarItem link="https://vecturagames.com" text="Home" />
        <NavbarItem link="https://blog.vecturagames.com" text="Blog" />
        <NavbarItem link="https://gpxviewer.com" text="GPX Viewer" />
        <NavbarItem link="https://trackbook.com" text="Trackbook" active />
      </ul>
    </nav>
  ) : null;
}

const useMobileMenuItemStyles = createStyles(
  (_, { active, isHovered }: { active: boolean; isHovered: boolean }, u) => ({
    link: {
      [u.dark]: {
        color: isHovered || active ? "#7950f2" : "#fff",
      },
      [u.light]: {
        color: isHovered || active ? "#7950f2" : "#000",
      },
    },
  })
);

function MobileMenuItem({
  text,
  link,
  active = false,
}: {
  text: string;
  link: string;
  active?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { classes } = useMobileMenuItemStyles({ active, isHovered });

  return (
    <li
      role="none"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <a className={classes.link} role="menuitem" href={link}>
        {text}
      </a>
    </li>
  );
}

const useMobileMenuStyles = createStyles(
  (
    theme,
    {
      isHovered,
      isMobileMenuOpen,
    }: { isHovered: boolean; isMobileMenuOpen: boolean },
    u
  ) => ({
    container: {
      [u.dark]: {
        backgroundColor: theme.colors.dark[8],
      },
      [u.light]: {
        backgroundColor: "#fff",
      },
      height: 190,
      marginTop: isMobileMenuOpen ? 0 : -190,
      transition: `margin-top ${PANEL_TOGGLE_ANIMATION_DURATION}s`,
    },
    icon: {
      [u.dark]: {
        backgroundColor: theme.colors.dark[8],
        color: isHovered ? "#7950f2" : "#fff",
      },
      [u.light]: {
        backgroundColor: "#fff",
        color: isHovered ? "#7950f2" : "#000",
      },
    },
  })
);

function MobileMenu() {
  const [isHovered, setIsHovered] = useState(false);
  const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();
  const { classes, cx } = useMobileMenuStyles({ isHovered, isMobileMenuOpen });

  return (
    <nav
      className={cx("mobile-menu", classes.container)}
      style={{
        height: 190,
        marginTop: isMobileMenuOpen ? 0 : -190,
        transition: `margin-top ${PANEL_TOGGLE_ANIMATION_DURATION}s`,
      }}
    >
      <ul role="menubar">
        <MobileMenuItem link="https://vecturagames.com" text="Home" />
        <MobileMenuItem link="https://blog.vecturagames.com" text="Blog" />
        <MobileMenuItem
          link="https://vecturagames.com/gpxviewer/"
          text="GPX Viewer"
        />
        <MobileMenuItem link="#" text="Trackbook" active />
      </ul>
      <a
        className="x-icon-btn"
        type="button"
        aria-expanded="false"
        aria-label="Toggle navigation"
        onClick={() => toggleMobileMenu()}
      >
        <i
          className={cx("fa", "fa-x", classes.icon)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          title="Toggle navigation"
        ></i>
      </a>
    </nav>
  );
}

export const MainContainer = () => {
  const { user } = useAuth();

  useInitialModals(user);

  return (
    <Box sx={{ width: "100%", height: "100%", position: "absolute" }}>
      <Navbar />
      <Group
        sx={{
          height: "100%",
          width: "100%",
          flexWrap: "nowrap",
          overflow: "hidden",
        }}
        gap={0}
      >
        <MobileMenu />
        <LeftMenuWrapper>
          <div></div>
          {user ? <AuthenticatedUserMenu /> : <UnauthenticatedUserMenu />}
        </LeftMenuWrapper>
        <Stack
          id="mapchartwrapper"
          sx={{
            height: "100%",
            flexGrow: 1,
            minWidth: 0,
            overflow: "hidden",
            position: "relative",
          }}
          gap={0}
        >
          <MapWrapper />
          <ChartPanel />
          <TopEditMenu />
        </Stack>
        <LegendPanel />
        {/* {isDev() && <DebugPanel />} */}
      </Group>
    </Box>
  );
};
